import React, { useState, useContext, useRef } from 'react';
import { 
  Box, Typography, Card, CardContent, IconButton, Button, 
  Drawer, Avatar, Divider, CircularProgress, Tooltip 
} from '@mui/material';
import { useHistory, useLocation } from 'react-router-dom';
import AuthContext from "flexbiz-core/components/auth/Context";
import { formatCurrency } from "flexbiz-core/utils";

// Component Quản lý thần thánh
import DataListManager from 'flexbiz-core/components/DataListManager'; 

// Icons
import PhoneInTalkIcon from '@mui/icons-material/PhoneInTalk';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import AssignmentIcon from '@mui/icons-material/Assignment';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';

import SecureFileDisplay from 'flexbiz-core/components/SecureFileDisplay';

const IOS_BLUE = '#007aff';

// Định nghĩa các giai đoạn của Phễu
const FUNNEL_STAGES = [
  { id: 'NEW', label: 'Mới tạo', color: '#8e8e93' },
  { id: 'CONSULTING', label: 'Đang tư vấn', color: IOS_BLUE },
  { id: 'VIEWING', label: 'Dẫn xem đất', color: '#ff9500' },
  { id: 'NEGOTIATING', label: 'Thương lượng', color: '#ff3b30' },
  { id: 'CLOSED', label: 'Chốt cọc', color: '#34c759' },
  { id: 'LOST', label: 'Thất bại/Hủy', color: '#1c1c1e' }
];

export default function CustomerFunnelPage() {
  const history = useHistory();
  const context = useContext(AuthContext);
  const location = useLocation();

  const queryParams = new URLSearchParams(location.search);
  const ma_kh = queryParams.get('ma_kh');

  // State quản lý Drawer chuyển trạng thái
  const [moveDrawerOpen, setMoveDrawerOpen] = useState(false);
  const [selectedDeal, setSelectedDeal] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);

  const managerRef = useRef(null);

  // ================= 1. CẤU HÌNH FIELDS CHO DATALIST MANAGER =================
  const dealFields = [
    { field: "ma_kh", label: "Khách hàng",hideInView:true, step: 1, stepLabel: "Khách hàng & cơ hội" , type: "Autocomplete", api: "customer", apiCondition: { status: true }, apiMapping: { value: "ma_kh", label: "ten_kh" } },
    {field:"ten_kh",hideInForm:true,isSubTitle:true},
    { field: "dien_thoai", label: "Số điện thoại", type: "String", step: 1 },
    { field: "ten_deal", label: "Tên cơ hội / Nhu cầu",isTitle:true, type: "String", required: true, multiline: true, step: 1 },
    { field: "ngan_sach", label: "Ngân sách dự kiến", type: "Number", step: 1 },

    { field: "ma_dt", label: "Dự án quan tâm", type: "Autocomplete",hideInView:true,hideInList:true, step: 1, api: "dmdt", apiCondition: { la_du_an_chu_dau_tu: true }, apiMapping: { value: "ma_dt", label: "ten_dt" }  },
    { field: "ten_dt", label: "Tên dự án", type: "String", step: 1,hideInForm:true },
    
    { field: "ma_nd_quan_tam", label: "Lô quan tâm", type: "String", step: 1,hideInView:true,hideInList:true },
    { field: "ten_nd_quan_tam", label: "Tên lô quan tâm", type: "String", step: 1,hideInForm:true },
    { 
      field: "funnel_stage", 
      label: "Giai đoạn phễu", 
      type: "Select", 
      options: FUNNEL_STAGES.map(s => ({ label: s.label, value: s.id })),
      readOnly: true, // Khóa ô này trong Form sửa để user bắt buộc dùng nút "Chuyển bước"
      helpText: "Dùng nút 'Chuyển bước' ở ngoài danh sách để cập nhật", step: 1,hideInForm:true
    },
    { field: "tai_lieu", label: "Hồ sơ đính kèm", step: 2, stepLabel: "Tài liệu" , type: "FileArray",accept: "image/png, image/jpeg,.pdf, .doc, .docx"},
  ];

  // ================= 2. HÀM TIỆN ÍCH =================
  const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.trim().split(' ');
    return parts[parts.length - 1].charAt(0).toUpperCase();
  };

  const formatDate = (isoString) => {
    if (!isoString) return 'Vừa tạo';
    return new Date(isoString).toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const getStageColor = (stageId) => {
    const stage = FUNNEL_STAGES.find(s => s.id === stageId);
    return stage ? stage.color : '#8e8e93';
  };

  const getStageLabel = (stageId) => {
    const stage = FUNNEL_STAGES.find(s => s.id === stageId);
    return stage ? stage.label : 'Chưa phân loại';
  };

  // ================= 3. XỬ LÝ CHUYỂN GIAI ĐOẠN =================
  const handleOpenMoveDrawer = (deal) => {
    setSelectedDeal(deal);
    setMoveDrawerOpen(true);
  };

  const handleMoveStage = async (newStageId) => {
    setIsUpdating(true);
    try {
      const payload = {
        _id: selectedDeal._id,
        funnel_stage: newStageId
      };
      
      // Gọi API bằng Object, nhận về Object
      const res = await context.apis.asyncPostList(context.userInfo.token, "deal", payload);
      
      if (res?.error || res?.data?.error) {
        context.alert("Lỗi cập nhật: " + (res?.error || res?.data?.error));
        return;
      }

      // 3. GỌI HÀM CỦA DATALIST ĐỂ CẬP NHẬT TRỰC TIẾP LÊN MÀN HÌNH MÀ KHÔNG FETCH LẠI
      if (managerRef.current) {
        managerRef.current.updateLocalItem({ 
          _id: selectedDeal._id, 
          funnel_stage: newStageId 
        });
      }
      
      setMoveDrawerOpen(false);
      context.alert("Chuyển giai đoạn thành công!");
    } catch (error) {
      console.error("Lỗi chuyển trạng thái:", error);
      context.alert("Cập nhật thất bại, vui lòng thử lại!", null, "red");
    } finally {
      setIsUpdating(false);
    }
  };

  // ================= 4. GIAO DIỆN TỪNG THẺ DEAL (RENDER ITEM) =================
  const renderDealCard = (deal, index, onEdit, onDelete,onView) => {
    const stageColor = getStageColor(deal.funnel_stage || 'NEW');

    return (
      <Card key={deal._id || index} sx={{ 
        //mb: 2,
        borderRadius: 4, 
        boxShadow: '0 4px 16px rgba(0,0,0,0.04)', 
        border: 'none', overflow: 'visible' 
       }}>
        
        {/* Dải màu đánh dấu giai đoạn ở cạnh trái */}
        <Box sx={{ display: 'flex' }}>
          <Box sx={{ width: 6, bgcolor: stageColor, borderTopLeftRadius: 16, borderBottomLeftRadius: 16 }} />
          
          <CardContent sx={{ p: '16px !important', flexGrow: 1 }}>
            
            {/* TIÊU ĐỀ & NÚT SỬA/XOÁ */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Avatar sx={{ bgcolor: stageColor, width: 44, height: 44, mr: 1.5, fontWeight: 'bold' }}>
                  {getInitials(deal.ten_kh)}
                </Avatar>
                <Box>
                  <Typography variant="subtitle1" fontWeight="bold" noWrap>
                    {deal.ten_kh || 'Khách hàng ẩn danh'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" fontWeight="500">
                    {deal.dien_thoai || '---'}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', gap: 0.5 }}>
                {deal.dien_thoai && (
                  <IconButton component="a" href={`tel:${deal.dien_thoai}`} size="small" sx={{ color: '#34c759', bgcolor: '#e8f5e9' }}>
                    <PhoneInTalkIcon fontSize="small" />
                  </IconButton>
                )}

                <Tooltip title="Chi tiết">
                  <IconButton size="small" onClick={onView} sx={{ color: '#ff9500', bgcolor: '#fff3e0' }}>
                    <VisibilityOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>

                <Tooltip title="Chỉnh sửa">
                  <IconButton size="small" onClick={onEdit} sx={{ color: '#ff9500', bgcolor: '#fff3e0' }}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Xoá">
                  <IconButton size="small" onClick={onDelete} sx={{ color: '#ff3b30', bgcolor: '#ffebee' }}>
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>

            {/* THÔNG TIN CHI TIẾT DEAL */}
            <Box sx={{ bgcolor: '#f2f2f7', p: 1.5, borderRadius: 3, mb: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 0.5 }}>
                <AssignmentIcon sx={{ fontSize: 16, color: IOS_BLUE, mr: 0.5, mt: 0.2 }} />
                <Typography variant="body2" color="#1c1c1e" fontWeight="600" sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                  {deal.ten_deal || deal.nhu_cau || 'Chưa ghi chú tên cơ hội'}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', color: '#ff3b30' }}>
                <AttachMoneyIcon sx={{ fontSize: 16, mr: 0.5 }} />
                <Typography variant="caption" fontWeight="bold">
                  Ngân sách: {deal.ngan_sach ? formatCurrency(deal.ngan_sach) : 'Đang cập nhật'}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ bgcolor: '#f2f2f7', p: 1.5, borderRadius: 3, mb: 1.5 }}>
                <Typography variant="caption">Hồ sơ đính kèm:</Typography>
                <SecureFileDisplay 
                    value={deal.tai_lieu} // Truyền dữ liệu trường FileArray
                    context={context} 
                />
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', color: '#8e8e93' }}>
                <AccessTimeIcon sx={{ fontSize: 14, mr: 0.5 }} />
                <Typography variant="caption">Tạo: {formatDate(deal.date_created)}</Typography>
              </Box>
              <Typography variant="caption" fontWeight="bold" sx={{ color: stageColor, bgcolor: `${stageColor}15`, px: 1, py: 0.5, borderRadius: 1 }}>
                {getStageLabel(deal.funnel_stage || 'NEW')}
              </Typography>
            </Box>

            

            <Divider sx={{ mb: 1.5, borderStyle: 'dashed' }} />

            {/* CÁC NÚT HÀNH ĐỘNG CHÍNH */}
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button 
                fullWidth variant="outlined" 
                startIcon={<SupportAgentIcon />}
                onClick={() => history.push(`/customer-care/${deal.ma_kh}`)}
                sx={{ textTransform: 'none', borderRadius: 3, color: IOS_BLUE, borderColor: 'rgba(0,122,255,0.3)', fontWeight: 'bold' }}
              >
                Chăm sóc
              </Button>
              
              <Button 
                fullWidth variant="contained" 
                startIcon={<SwapHorizIcon />}
                onClick={() => handleOpenMoveDrawer(deal)}
                sx={{ textTransform: 'none', borderRadius: 3, bgcolor: stageColor, color: 'white', boxShadow: 'none', fontWeight: 'bold' }}
              >
                Chuyển bước
              </Button>
            </Box>

          </CardContent>
        </Box>
      </Card>
    );
  };

  return (
    <>
      <DataListManager
        title="Phễu Bán Hàng"
        code="deal"
        ref={managerRef}
        
        condition={{ user_created: context.userInfo?.username, ...(ma_kh && { ma_kh }) }}
        fields={dealFields}
        
        // Sử dụng Quick Filter để làm Tabs chuyển đổi giai đoạn
        quickFilterConfig={{
          field: "funnel_stage",
          options: FUNNEL_STAGES.map(s => ({ label: s.label, value: s.id }))
        }}

        defaultValues={{
          funnel_stage: 'NEW',
          ma_kh: ma_kh || ''
        }}

        canAdd={true}
        canEdit={true}
        canDelete={true}
        onBack={() => history.goBack()}
        renderItem={renderDealCard}
        actionBarPosition="bottom"
      />

      {/* DRAWER CHUYỂN GIAI ĐOẠN (Giữ nguyên từ bản gốc) */}
      <Drawer
        anchor="bottom"
        open={moveDrawerOpen}
        onClose={() => !isUpdating && setMoveDrawerOpen(false)}
        PaperProps={{ sx: { borderTopLeftRadius: 20, borderTopRightRadius: 20, pb: 4, px: 2, pt: 1, bgcolor: '#ffffff' } }}
      >
        <Box sx={{ display: 'flex', justifyItems: 'center', justifyContent: 'center', mb: 1 }}>
          <Box sx={{ width: 40, height: 5, bgcolor: '#d1d1d6', borderRadius: 3 }} />
        </Box>
        
        <Box sx={{ mb: 3, mt: 1, textAlign: 'center' }}>
          <Typography variant="h6" fontWeight="bold">Cập nhật tiến độ</Typography>
          <Typography variant="body2" color="text.secondary">
            Cơ hội: <Typography component="span" fontWeight="bold" color="primary" sx={{ display: 'block' }}>{selectedDeal?.ten_deal}</Typography>
          </Typography>
        </Box>

        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
          Chọn giai đoạn tiếp theo:
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {FUNNEL_STAGES.map((stage) => {
            const isCurrent = (selectedDeal?.funnel_stage || 'NEW') === stage.id;
            return (
              <Button
                key={stage.id}
                variant={isCurrent ? "outlined" : "contained"}
                fullWidth
                disabled={isCurrent || isUpdating}
                endIcon={
                  isUpdating && selectedDeal?.funnel_stage !== stage.id ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : isCurrent ? (
                    <CheckCircleIcon />
                  ) : null
                }
                onClick={() => handleMoveStage(stage.id)}
                sx={{
                  justifyContent: 'flex-start',
                  py: 1.5, px: 2,
                  borderRadius: 3,
                  textTransform: 'none',
                  fontSize: '1.05rem',
                  fontWeight: 'bold',
                  bgcolor: isCurrent ? 'transparent' : `${stage.color}15`,
                  color: stage.color,
                  border: isCurrent ? `2px solid ${stage.color}` : 'none',
                  boxShadow: 'none',
                  '&:hover': { bgcolor: `${stage.color}30`, boxShadow: 'none' }
                }}
              >
                {stage.label}
              </Button>
            );
          })}
        </Box>
      </Drawer>
    </>
  );
}